import { computed, shallowRef } from 'vue'
import { storage as localStorage } from '../utils/storage'
import { getCookieClient, setCookieClient } from '../utils/storage/utils'
import {
  consentSnapshot,
  subscribeToConsent,
} from '../utils/consent/consentGate'
import {
  INTEREST_COOKIE,
  formatInterestCookie,
  type InterestCookie,
} from '../utils/interestCookie'
import type { NewsArticle } from '../server/models/news/news-article'

export const LOCAL_STORAGE_KEY_TASTE_PROFILE = 'app:tasteProfile'
export const LOCAL_STORAGE_KEY_INTEREST_MATCH = 'app:interestMatch'
export const LOCAL_STORAGE_KEY_INTEREST_BACKOFF = 'app:interestBackoffUntil'

export const TASTE_PROFILE_LIMIT = 20
const DEFAULT_BACKOFF_SECONDS = 300
const EMPTY_MATCH_MAX_SECONDS = 30 * 60

type TasteClick = {
  id: string
  embedding: string
  modelVersion: string
  at: number
}

export type InterestSegment = {
  taxonomy: string
  segtax: number
  id: string
  name: string
  political: boolean
}

export type InterestsRes = {
  catalog_version: string
  expires_in_seconds: number
  interests: string[]
  segments: InterestSegment[]
  keywords: string[]
}

type InterestMatch = {
  catalogVersion: string
  interests: string[]
  segments: InterestSegment[]
  keywords: string[]
  expiresAt: number
  lastClickId?: string
}

export function decodeEmbedding(base64: string): number[] {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
  return Array.from(new Int8Array(bytes.buffer))
}

export function averageEmbedding(vectors: number[][]): number[] {
  const sum = vectors[0].map((_, index) =>
    vectors.reduce((total, vector) => total + vector[index], 0)
  )
  const length = Math.hypot(...sum)
  return sum.map((value) => value / length)
}

function readJson<T>(key: string): T | null {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null')
  } catch {
    return null
  }
}

function readProfile(): TasteClick[] {
  const stored = readJson<unknown>(LOCAL_STORAGE_KEY_TASTE_PROFILE)
  return Array.isArray(stored) ? stored : []
}

function isStale(stored: InterestMatch): boolean {
  if (stored.expiresAt <= Date.now()) return true
  if (stored.interests.length) return false
  return stored.lastClickId !== readProfile().at(-1)?.id
}

function isInterestMatch(stored: unknown): stored is InterestMatch {
  return (
    typeof stored === 'object' &&
    stored !== null &&
    'catalogVersion' in stored &&
    typeof stored.catalogVersion === 'string' &&
    'expiresAt' in stored &&
    typeof stored.expiresAt === 'number' &&
    'interests' in stored &&
    Array.isArray(stored.interests) &&
    'segments' in stored &&
    Array.isArray(stored.segments) &&
    'keywords' in stored &&
    Array.isArray(stored.keywords)
  )
}

function readMatch(): InterestMatch | null {
  const stored = readJson<unknown>(LOCAL_STORAGE_KEY_INTEREST_MATCH)
  return isInterestMatch(stored) && !isStale(stored) ? stored : null
}

export function withoutClickedStories<T extends { id: string }>(
  items: T[]
): T[] {
  const clicked = new Set(readProfile().map((click) => click.id))
  return items.filter((item) => !clicked.has(item.id))
}

const match = shallowRef<InterestMatch | null>(
  import.meta.client ? readMatch() : null
)

export function clearInterestCookie() {
  if (getCookieClient(INTEREST_COOKIE) === null) return
  setCookieClient(INTEREST_COOKIE, '', 0)
}

function writeInterestCookie(current: InterestMatch) {
  const consent = consentSnapshot()?.firstPartyId ?? 'unknown'
  if (consent === 'unknown') return
  const interest = current.interests[0]
  if (consent === 'deny' || !interest) {
    clearInterestCookie()
    return
  }
  setCookieClient(
    INTEREST_COOKIE,
    formatInterestCookie({ interest, catalogVersion: current.catalogVersion }),
    Math.floor((current.expiresAt - Date.now()) / 1000),
    { secure: location.protocol === 'https:' }
  )
}

function matchLifetimeSeconds(response: InterestsRes): number {
  if (response.interests.length) return response.expires_in_seconds
  return Math.min(response.expires_in_seconds, EMPTY_MATCH_MAX_SECONDS)
}

function saveMatch(response: InterestsRes, lastClickId: string) {
  match.value = {
    catalogVersion: response.catalog_version,
    interests: response.interests,
    segments: response.segments,
    keywords: response.keywords,
    expiresAt: Date.now() + matchLifetimeSeconds(response) * 1000,
    lastClickId,
  }
  localStorage.setItem(
    LOCAL_STORAGE_KEY_INTEREST_MATCH,
    JSON.stringify(match.value)
  )
  writeInterestCookie(match.value)
}

function backOff(retryAfter: string | null) {
  const seconds = Number(retryAfter) || DEFAULT_BACKOFF_SECONDS
  localStorage.setItem(
    LOCAL_STORAGE_KEY_INTEREST_BACKOFF,
    String(Date.now() + seconds * 1000)
  )
}

function isBackingOff(): boolean {
  const until = Number(localStorage.getItem(LOCAL_STORAGE_KEY_INTEREST_BACKOFF))
  return until > Date.now()
}

let pendingMatch: Promise<void> | null = null

async function requestMatch(profile: TasteClick[]) {
  const response = await $fetch
    .raw<InterestsRes>('/api/v2/interests', {
      method: 'POST',
      ignoreResponseError: true,
      body: {
        model_version: profile[0].modelVersion,
        vectors: [
          averageEmbedding(
            profile.map((click) => decodeEmbedding(click.embedding))
          ),
        ],
      },
    })
    .catch(() => null)
  if (response?.ok && response._data) {
    saveMatch(response._data, profile[profile.length - 1].id)
  } else {
    backOff(response?.headers.get('retry-after') ?? null)
  }
}

function refreshMatch() {
  const profile = readProfile()
  if (pendingMatch || !profile.length || isBackingOff()) return
  pendingMatch = requestMatch(profile).finally(() => {
    pendingMatch = null
  })
}

export function recordTasteClick(article: NewsArticle | undefined) {
  if (!article?.embedding || !article.embeddingModelVersion) return
  const click: TasteClick = {
    id: article.id,
    embedding: article.embedding,
    modelVersion: article.embeddingModelVersion,
    at: Date.now(),
  }
  const profile = readProfile().filter(
    (stored) =>
      stored.modelVersion === click.modelVersion && stored.id !== click.id
  )
  localStorage.setItem(
    LOCAL_STORAGE_KEY_TASTE_PROFILE,
    JSON.stringify([...profile, click].slice(-TASTE_PROFILE_LIMIT))
  )
}

let consentSubscription: (() => void) | null = null

export function syncInterestCookie(isCupcakeFeed: boolean) {
  if (!isCupcakeFeed) {
    clearInterestCookie()
    return
  }
  consentSubscription ??= subscribeToConsent(() => {
    if (match.value) writeInterestCookie(match.value)
  })
  match.value = readMatch()
  if (!match.value) {
    clearInterestCookie()
    refreshMatch()
    return
  }
  writeInterestCookie(match.value)
}

export const useTasteProfile = () => {
  const feedInterest = (): InterestCookie | null => {
    syncInterestCookie(true)
    const interest = match.value?.interests[0]
    if (!match.value || !interest) return null
    return { interest, catalogVersion: match.value.catalogVersion }
  }

  const dropInterestMatch = () => {
    match.value = null
    localStorage.removeItem(LOCAL_STORAGE_KEY_INTEREST_MATCH)
    clearInterestCookie()
  }

  return {
    feedInterest,
    dropInterestMatch,
    segments: computed(() => match.value?.segments ?? []),
    keywords: computed(() => match.value?.keywords ?? []),
  }
}
